import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Bot,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Circle,
  Clock,
  FileDown,
  FileText,
  Info,
  Loader2,
  Send,
  Upload,
  UserCheck,
  X,
  XCircle,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { UnlockScreen } from "@/components/freemium/UnlockScreen";
import { useEnrollment } from "@/contexts/EnrollmentContext";
import { useUserProfile } from "@/hooks/useUserProfile";
import DocumentUpload from "../components/DocumentUpload";
import { InlineCitationLight } from "../components/ui/inline-citation";
import { api } from "../services/api";
import { pollReviewStatus, resubmitDocument, submitReview } from "../services/reviewApi";
import type { Assignment, Review, ReviewStatus, Submission, UploadState } from "../types";

/** Attempts allowed when the cohort hasn't told us its own cap yet — matches
 *  the API's DEFAULT_MAX_SUBMISSIONS so a slow/failed assignment fetch never
 *  shows a smaller allowance than the participant actually has. */
const DEFAULT_MAX_SUBMISSIONS = 3;

interface UserAssignmentWithTopic {
  id: string;
  topic_id: string | null;
  status: string;
  topic: {
    id: string;
    title: string;
    description: string;
  } | null;
}

/** What to show a participant when a submit fails.
 *
 * `ReviewApiError` already carries the API's `detail` (deadline passed, already
 * submitted, wrong file type), which is the useful half. Anything else — a
 * server 500, a dropped connection — gets a plain instruction rather than a
 * stack-shaped string, because the one thing a participant must not conclude is
 * that nothing happened. */
function errorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  return "Something went wrong submitting your document. Please try again, and contact support if it keeps failing.";
}

export default function UploadPage() {
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const { userProfile, isLoading: isLoadingProfile } = useUserProfile();
  const { enrollment, refetch: refetchEnrollment } = useEnrollment();
  // Free Participants see the unlock screen right after submitting their final Work Package (KC-498)
  const [showUnlock, setShowUnlock] = useState(false);
  const [assignment, setAssignment] = useState<Assignment | null>(null);
  const [userAssignment, setUserAssignment] = useState<UserAssignmentWithTopic | null>(null);
  const [isLoadingAssignment, setIsLoadingAssignment] = useState(true);
  const [isLoadingUserAssignment, setIsLoadingUserAssignment] = useState(true);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadState, setUploadState] = useState<UploadState>("idle");
  const [progressMessage, setProgressMessage] = useState("");
  // A failed submit used to be logged and swallowed, so the button looked inert
  // and the attachment vanished on navigation — participants read that as "the
  // platform ate my work" and stopped trying (KC-469).
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [deadlinePassed, setDeadlinePassed] = useState(false);
  const cancelPollRef = useRef<(() => void) | null>(null);

  // Timeline data
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [reviews, setReviews] = useState<Map<string, Review>>(new Map());
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [showResubmit, setShowResubmit] = useState(false);
  const [reviewModalData, setReviewModalData] = useState<{
    review: Review;
    isFinal: boolean;
  } | null>(null);

  const toggleSection = (sectionKey: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionKey)) next.delete(sectionKey);
      else next.add(sectionKey);
      return next;
    });
  };

  const getScoreColorClass = (score: number, maxScore: number = 20) => {
    const pct = score / maxScore;
    if (pct >= 0.8) return "text-green-700";
    if (pct >= 0.6) return "text-amber-700";
    return "text-red-700";
  };

  const formatSectionName = (key: string) => {
    return key
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ")
      .replace(" And ", " & ");
  };

  const loadAssignment = useCallback(async () => {
    if (!assignmentId) return;
    try {
      const data = await api.getAssignment(assignmentId);
      setAssignment(data);
    } catch (error) {
      console.error("Failed to load assignment:", error);
    } finally {
      setIsLoadingAssignment(false);
    }
  }, [assignmentId]);

  const loadUserData = useCallback(async () => {
    if (isLoadingProfile || !userProfile || !assignmentId) return;

    try {
      const ua = await api.getUserAssignmentByAssignment(assignmentId);
      setUserAssignment(ua);

      if (ua?.id) {
        // Load all submissions for this user_assignment
        const allSubmissions = await api.getAllSubmissionsByUserAssignment(ua.id);
        setSubmissions(allSubmissions);

        // Load reviews for each submission
        const reviewMap = new Map<string, Review>();
        for (const sub of allSubmissions) {
          try {
            const review = await api.getReviewBySubmission(sub.id);
            if (review) {
              reviewMap.set(sub.id, review);
            }
          } catch {
            // No review for this submission
          }
        }
        setReviews(reviewMap);

        // Resume polling if the latest submission has a pending review
        const latestSub = allSubmissions[allSubmissions.length - 1];
        if (latestSub) {
          const latestReview = reviewMap.get(latestSub.id);
          if (latestReview?.status === "pending") {
            cancelPollRef.current?.();
            setUploadState("reviewing");
            setProgressMessage("Reviewing your document...");
            cancelPollRef.current = pollReviewStatus(
              latestReview.id,
              async () => {
                setProgressMessage("Review complete!");
                await new Promise((resolve) => setTimeout(resolve, 300));
                await loadUserData();
                await refetchEnrollment();
                setUploadState("idle");
              },
              async () => {
                await loadUserData();
                setUploadState("idle");
              }
            );
          } else if (latestReview?.status === "failed") {
            setUploadState("idle");
          }
        }
      }
    } catch (error) {
      console.error("Failed to load user data:", error);
    } finally {
      setIsLoadingUserAssignment(false);
    }
  }, [isLoadingProfile, userProfile, assignmentId, refetchEnrollment]);

  useEffect(() => {
    loadAssignment();
  }, [loadAssignment]);

  useEffect(() => {
    loadUserData();
  }, [loadUserData]);

  useEffect(() => {
    if (assignment) {
      const now = new Date();
      const deadline = new Date(assignment.deadline);
      setDeadlinePassed(now > deadline);
    }
  }, [assignment]);

  // Cancel polling when navigating away
  useEffect(() => {
    return () => {
      cancelPollRef.current?.();
    };
  }, []);

  const hasTopicAssigned = userAssignment?.topic_id !== null && userAssignment?.topic !== null;

  const handleFileSelect = (file: File | null) => {
    setSelectedFile(file);
    setUploadState("idle");
    setSubmitError(null);
  };

  const offerUnlockAfterFinalSubmission = () => {
    if (assignment?.is_final && enrollment?.programme?.is_freemium) {
      setShowUnlock(true);
    }
  };

  const handleSubmit = async () => {
    if (!selectedFile || !assignmentId || !userProfile) return;
    setUploadState("reviewing");
    setSubmitError(null);
    setProgressMessage("Uploading your document...");

    try {
      const { reviewId } = await submitReview(selectedFile, assignmentId, userProfile.id);
      setProgressMessage("Reviewing your document...");

      cancelPollRef.current?.();
      cancelPollRef.current = pollReviewStatus(
        reviewId,
        async () => {
          setProgressMessage("Review complete!");
          await new Promise((resolve) => setTimeout(resolve, 300));
          await loadUserData();
          await refetchEnrollment();
          setSelectedFile(null);
          setUploadState("idle");
          offerUnlockAfterFinalSubmission();
        },
        async () => {
          await loadUserData();
          setUploadState("idle");
        }
      );
    } catch (error) {
      console.error("Submit error:", error);
      // Keep the attachment: the participant should be able to retry without
      // re-picking the file they just chose.
      setSubmitError(errorMessage(error));
      setUploadState("idle");
    }
  };

  const handleResubmit = async () => {
    if (!selectedFile || !assignmentId || !userProfile) return;
    setUploadState("reviewing");
    setSubmitError(null);
    setProgressMessage("Uploading your updated document...");

    try {
      const { reviewId } = await resubmitDocument(selectedFile, assignmentId, userProfile.id);
      setProgressMessage("Reviewing your updated document...");

      cancelPollRef.current?.();
      cancelPollRef.current = pollReviewStatus(
        reviewId,
        async () => {
          setProgressMessage("Document reviewed and submitted!");
          await new Promise((resolve) => setTimeout(resolve, 300));
          setIsLoadingUserAssignment(true);
          await loadUserData();
          await refetchEnrollment();
          setSelectedFile(null);
          setUploadState("idle");
          setShowResubmit(false);
          offerUnlockAfterFinalSubmission();
        },
        async () => {
          await loadUserData();
          setUploadState("idle");
        }
      );
    } catch (error) {
      console.error("Resubmit error:", error);
      setSubmitError(errorMessage(error));
      setUploadState("idle");
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }),
      time: date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }),
    };
  };

  // Helpers for timeline state
  const getLatestSubmission = () => submissions[submissions.length - 1] || null;
  const getLatestReview = () => {
    const latest = getLatestSubmission();
    return latest ? reviews.get(latest.id) || null : null;
  };
  const latestReview = getLatestReview();
  const latestReviewStatus = latestReview?.status as ReviewStatus | undefined;
  const isAiReviewed = latestReviewStatus === "ai_reviewed";
  const isSubmittedForReview = latestReviewStatus === "submitted_for_review";
  const isAdminConfirmed =
    latestReviewStatus === "admin_confirmed" || latestReviewStatus === "admin_updated";
  const maxSubmissions = assignment?.max_submissions ?? DEFAULT_MAX_SUBMISSIONS;
  const canResubmit = isAiReviewed && submissions.length < maxSubmissions;
  const remainingSubmissions = Math.max(0, maxSubmissions - submissions.length);
  const hasSubmissions = submissions.length > 0;

  // Render expandable AI review sections
  const renderReviewSections = (review: Review, prefix: string, showScores: boolean) => {
    const sections = review.admin_sections || review.sections || {};

    return (
      <div className="space-y-2 mt-3">
        {Object.entries(sections)
          .filter(([key]) => key !== "_meta")
          .map(([key, section]: [string, any]) => {
            const sectionKey = `${prefix}_${key}`;
            const isExpanded = expandedSections.has(sectionKey);
            const features =
              section.observable_features_detected || section.detected_behaviours || [];
            const missing =
              section.missing_features_for_next_band || section.missing_behaviours || [];

            return (
              <div
                key={sectionKey}
                className="rounded-xl border border-content-border bg-content-subtle/50 overflow-hidden"
              >
                <button
                  onClick={() => toggleSection(sectionKey)}
                  className="w-full flex items-center justify-between p-3 hover:bg-content transition-colors text-left"
                >
                  <div className="flex items-center gap-2">
                    {isExpanded ? (
                      <ChevronDown className="w-3 h-3 text-content-muted" />
                    ) : (
                      <ChevronRight className="w-3 h-3 text-content-muted" />
                    )}
                    <span className="text-sm text-content-foreground">
                      {formatSectionName(key)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {(section.band || section.command_word_level) && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-content-subtle border border-content-border text-content-muted">
                        {section.band || section.command_word_level}
                      </span>
                    )}
                    {showScores && (
                      <span
                        className={`text-sm font-bold ${getScoreColorClass(section.score, section.max_score || 20)}`}
                      >
                        {section.score}/{section.max_score || 20}
                      </span>
                    )}
                  </div>
                </button>

                {isExpanded && (
                  <div className="px-3 pb-3 pt-1 border-t border-content-border">
                    <div className="space-y-3 pl-5">
                      {(section.diagnostic_explanation || section.short_rationale) && (
                        <p className="text-xs text-content-muted italic">
                          <InlineCitationLight
                            text={section.diagnostic_explanation || section.short_rationale}
                          />
                        </p>
                      )}
                      {features.length > 0 && (
                        <div>
                          <h5 className="text-xs font-medium uppercase tracking-wider text-green-700 mb-1">
                            Observable Features
                          </h5>
                          <ul className="space-y-1">
                            {features.map((s: string, idx: number) => (
                              <li
                                key={idx}
                                className="text-xs text-content-muted pl-2 border-l-2 border-green-300"
                              >
                                <InlineCitationLight text={s} />
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {missing.length > 0 && (
                        <div>
                          <h5 className="text-xs font-medium uppercase tracking-wider text-amber-700 mb-1">
                            To Reach Next Band
                          </h5>
                          <ul className="space-y-1">
                            {missing.map((s: string, idx: number) => (
                              <li
                                key={idx}
                                className="text-xs text-content-muted pl-2 border-l-2 border-amber-300"
                              >
                                <InlineCitationLight text={s} />
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
      </div>
    );
  };

  if (isLoadingAssignment || isLoadingUserAssignment) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-content-muted" />
      </div>
    );
  }

  if (!assignment) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-content-muted">Work package not found</p>
      </div>
    );
  }

  const opened = formatDate(assignment.start_date);
  const due = formatDate(assignment.deadline);
  const topic = userAssignment?.topic;
  // Global work-package number (1..N) supplied by the API; the "unweighted"
  // practice run is the earliest window (WP 1), not a hard-coded week/assignment.
  const workPackageNumber =
    assignment.work_package_number ??
    (assignment.week_number - 1) * 2 + assignment.assignment_number;
  const isUnweighted = workPackageNumber === 1;

  const pageHeader = (
    <div className="mb-6">
      <Link
        to="/timeline"
        className="mb-5 inline-flex items-center gap-2 text-[13px] font-semibold text-[#5C6E63] transition-colors hover:text-[#1E6F4F]"
      >
        <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2} />
        Back to Work Packages
      </Link>
      <div className="flex flex-wrap items-center gap-2.5">
        <span className="text-[11px] font-bold uppercase tracking-[.16em] text-content-muted">
          Week {assignment.week_number} · Work Package {workPackageNumber}
        </span>
        <span
          className={`whitespace-nowrap rounded-full border px-2.5 py-[5px] text-[10.5px] font-bold tracking-[.1em] ${
            deadlinePassed
              ? "border-[#EACDC8] bg-[#FBF0EE] text-[#A41720]"
              : "border-[#CBE3D2] bg-[#F0F8F2] text-[#1E6F4F]"
          }`}
        >
          {deadlinePassed ? "DEADLINE PASSED" : "OPEN FOR SUBMISSION"}
        </span>
        {isUnweighted && (
          <span className="whitespace-nowrap rounded-full border border-[#E4D3A1] bg-[#FBF6E7] px-2.5 py-[5px] text-[10.5px] font-bold tracking-[.1em] text-[#8A6D1F]">
            UNWEIGHTED · PRACTICE RUN
          </span>
        )}
      </div>
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto py-8 px-4">
      {/* No Topic Assigned State */}
      {!hasTopicAssigned ? (
        <>
          {pageHeader}
          <h1 className="mb-6 text-3xl font-extrabold tracking-[-.015em] text-content-foreground">
            Week {assignment.week_number} · Work Package {workPackageNumber}
          </h1>
          <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-content-foreground font-medium">Topic Not Assigned</h3>
              <p className="text-content-muted text-sm">
                Your topic for this work package has not been assigned yet. Please contact your
                administrator to have a topic assigned before you can submit.
              </p>
            </div>
          </div>
        </>
      ) : (
        <>
          {pageHeader}

          {/* Topic Title */}
          <h1 className="mb-6 text-3xl sm:text-[34px] font-extrabold leading-[1.2] tracking-[-.015em] text-content-foreground">
            {topic?.title}
          </h1>

          {/* Date Boxes */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="flex-1 rounded-xl bg-card border border-content-border px-5 py-4">
              <div className="flex items-center gap-2 text-content-muted text-[11px] font-bold uppercase tracking-[.14em] mb-1">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <span>Opened</span>
              </div>
              <div className="text-lg font-bold text-content-foreground">{opened.date}</div>
              <div className="text-content-muted text-sm">{opened.time}</div>
            </div>
            <div className="flex-1 rounded-xl bg-card border border-content-border px-5 py-4">
              <div className="flex items-center gap-2 text-content-muted text-[11px] font-bold uppercase tracking-[.14em] mb-1">
                <Clock className="w-4 h-4 text-amber-600" />
                <span>Due</span>
              </div>
              <div className="text-lg font-bold text-content-foreground">{due.date}</div>
              <div className="text-content-muted text-sm">
                {due.time} · version present at close is final
              </div>
            </div>
          </div>

          {/* Description */}
          {topic?.description && (
            <div className="mb-6 rounded-[14px] border border-content-border bg-card px-6 py-5">
              <h3 className="text-[11px] font-bold uppercase tracking-[.16em] text-content-muted mb-2.5">
                Description
              </h3>
              <p className="text-content-foreground leading-relaxed font-serif text-[16.5px]">
                {topic.description}
              </p>
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <span className="mr-1 text-[11px] font-bold uppercase tracking-[.16em] text-content-muted">
                  Assessed against
                </span>
                {[
                  "Research Integrity",
                  "Analytical Reasoning",
                  "Relevance & Investment Impact",
                  "Clarity & Communication",
                  "Professional & Ethical Standards",
                ].map((criterion) => (
                  <span
                    key={criterion}
                    className="whitespace-nowrap rounded-full border border-content-border bg-content-subtle px-3 py-1 text-xs font-semibold text-content-foreground"
                  >
                    {criterion}
                  </span>
                ))}
                <Link
                  to="/marking-guide"
                  className="inline-flex items-center gap-1 text-xs font-bold text-[#1E6F4F] hover:underline"
                >
                  How feedback works
                  <ArrowRight className="h-3 w-3" strokeWidth={2.2} />
                </Link>
              </div>
            </div>
          )}

          {/* ============================================================ */}
          {/* SUBMIT YOUR WORK CARD                                        */}
          {/* ============================================================ */}

          <div className="relative overflow-hidden rounded-[14px] border border-content-border bg-card px-6 py-6 sm:px-7">
            <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-brand to-green-700/0" />
            <h3 className="mb-5 text-[11px] font-bold uppercase tracking-[.16em] text-content-muted">
              Submit Your Work
            </h3>

            {/* Submitted banner */}
            {uploadState === "idle" && hasSubmissions && getLatestSubmission() && (
              <div className="mb-5 flex items-start gap-3.5 rounded-xl border border-[#CBE3D2] bg-[#F0F8F2] px-5 py-4">
                <CheckCircle
                  className="mt-0.5 h-5 w-5 flex-none text-[#1E6F4F]"
                  strokeWidth={1.8}
                />
                <div className="min-w-0">
                  <div className="truncate text-[15px] font-bold text-content-foreground">
                    {(() => {
                      const s = getLatestSubmission();
                      return `Submitted: ${s?.original_filename ?? s?.file_path.split("/").pop()}`;
                    })()}
                  </div>
                  <div className="mt-0.5 text-[13.5px] text-[#3E5A4B]">
                    {deadlinePassed && !isAdminConfirmed
                      ? "The window has closed — your final version is with the Executive Review team for sign-off."
                      : canResubmit && !deadlinePassed
                        ? `You can resubmit ${remainingSubmissions === 1 ? "once more" : `up to ${remainingSubmissions} more times`} until ${due.date}, ${due.time}. Your final version receives the Executive Review sign-off.`
                        : "Your final version receives the Executive Review sign-off."}
                  </div>
                </div>
              </div>
            )}

            {/* ============================================================ */}
            {/* ACTIVITY TIMELINE                                            */}
            {/* ============================================================ */}

            {uploadState === "idle" && hasSubmissions && (
              <div className="mb-8">
                <h3 className="text-content-muted text-xs uppercase tracking-wider mb-4">
                  Activity
                </h3>

                <div className="relative">
                  {/* Timeline line */}
                  <div className="absolute left-[11px] top-3 bottom-0 w-px bg-content-border" />

                  <div className="space-y-0">
                    {(() => {
                      const latestSubId = getLatestSubmission()?.id;
                      return submissions.map((sub, _subIdx) => {
                        const review = reviews.get(sub.id);
                        const meta = review?.sections?._meta as any;
                        const isRelevant = meta?.is_relevant ?? true;
                        const isFirstSubmission = sub.revision_number === 1;

                        // Only the latest submission can carry the final review badge
                        const isFinalReview =
                          sub.id === latestSubId &&
                          (review?.status === "admin_confirmed" ||
                            review?.status === "admin_updated");
                        const displaySections = isFinalReview
                          ? review?.admin_sections || review?.sections
                          : review?.sections;
                        const displayScore =
                          isFinalReview && review?.admin_final_score != null
                            ? review.admin_final_score
                            : review?.final_score;

                        return (
                          <div key={sub.id}>
                            {/* Upload event */}
                            <div className="relative flex items-start gap-4 pb-6">
                              <div className="relative z-10 flex-shrink-0 w-6 h-6 rounded-full bg-brand/10 border border-brand/30 flex items-center justify-center">
                                <FileText className="w-3 h-3 text-brand" />
                              </div>
                              <div className="flex-1 min-w-0 pt-0.5">
                                <div className="flex items-center gap-3">
                                  <span className="text-sm text-content-foreground font-medium">
                                    {isFirstSubmission
                                      ? "Document uploaded"
                                      : "Revised document uploaded"}
                                  </span>
                                  <span className="text-xs text-content-muted">
                                    {formatDateTime(sub.submitted_at)}
                                  </span>
                                </div>
                                <button
                                  type="button"
                                  onClick={async () => {
                                    const url = await api.getSubmissionFileUrl(sub.id);
                                    window.open(url, "_blank");
                                  }}
                                  className="inline-flex items-center gap-1.5 text-xs text-brand hover:text-brand-foreground mt-1 transition-colors"
                                >
                                  <FileDown className="w-3 h-3" />
                                  {sub.original_filename ?? sub.file_path.split("/").pop()}
                                </button>
                              </div>
                            </div>

                            {/* AI Review event — skip pending (spinner shown above), show failed as error */}
                            {review &&
                              review.status !== "pending" &&
                              review.status === "failed" && (
                                <div className="relative flex items-start gap-4 pb-6">
                                  <div className="relative z-10 flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center bg-red-50 border border-red-300">
                                    <XCircle className="w-3 h-3 text-red-700" />
                                  </div>
                                  <div className="flex-1 min-w-0 pt-0.5">
                                    <span className="text-sm text-red-700 font-medium">
                                      Review failed
                                    </span>
                                    <p className="text-xs text-content-muted mt-0.5">
                                      Please contact your administrator.
                                    </p>
                                  </div>
                                </div>
                              )}

                            {review &&
                              review.status !== "pending" &&
                              review.status !== "failed" && (
                                <div className="relative flex items-start gap-4 pb-6">
                                  <div
                                    className={`relative z-10 flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${
                                      isFinalReview
                                        ? "bg-green-50 border border-green-300"
                                        : "bg-blue-50 border border-blue-300"
                                    }`}
                                  >
                                    {isFinalReview ? (
                                      <UserCheck className="w-3 h-3 text-green-700" />
                                    ) : (
                                      <Bot className="w-3 h-3 text-blue-700" />
                                    )}
                                  </div>
                                  <div className="flex-1 min-w-0 pt-0.5">
                                    <div className="flex items-center gap-3">
                                      <span className="text-sm text-content-foreground font-medium">
                                        {isFinalReview
                                          ? "Final review published"
                                          : "AI review completed"}
                                      </span>
                                      <span className="text-xs text-content-muted">
                                        {formatDateTime(
                                          isFinalReview
                                            ? review.admin_reviewed_at ||
                                                review.reviewed_at ||
                                                review.created_at
                                            : review.reviewed_at || review.created_at
                                        )}
                                      </span>
                                    </div>

                                    {/* Score + relevance summary */}
                                    <div className="mt-2">
                                      <div className="flex items-center gap-3">
                                        {isFinalReview && (
                                          <>
                                            <span
                                              className={`text-2xl font-bold ${
                                                (displayScore || 0) >= 60
                                                  ? "text-green-700"
                                                  : (displayScore || 0) >= 40
                                                    ? "text-amber-700"
                                                    : "text-red-700"
                                              }`}
                                            >
                                              {displayScore}
                                            </span>
                                            <span className="text-content-muted text-sm">/100</span>
                                          </>
                                        )}
                                        {!isRelevant && !isFinalReview && (
                                          <span className="text-xs text-amber-700 px-2 py-0.5 bg-amber-50 border border-amber-200 uppercase tracking-wider">
                                            Not Relevant
                                          </span>
                                        )}
                                        {isFinalReview && (
                                          <span className="text-xs text-green-700 px-2 py-0.5 bg-green-50 border border-green-200 uppercase tracking-wider">
                                            Final Grade
                                          </span>
                                        )}
                                      </div>

                                      {/* Relevance warning for AI reviews */}
                                      {!isRelevant &&
                                        !isFinalReview &&
                                        meta?.relevance_explanation && (
                                          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 mt-2">
                                            <p className="text-xs text-amber-800">
                                              <strong>Relevance: {meta.relevance_score}/100</strong>{" "}
                                              — {meta.relevance_explanation}
                                            </p>
                                          </div>
                                        )}

                                      {/* AI disclaimer for non-final reviews */}
                                      {!isFinalReview && (
                                        <div className="flex items-start gap-2 mt-2">
                                          <Info className="w-3 h-3 text-blue-600 flex-shrink-0 mt-0.5" />
                                          <p className="text-xs text-content-muted">
                                            AI-generated feedback. Final grade will be provided by a
                                            human reviewer.
                                          </p>
                                        </div>
                                      )}

                                      {/* View feedback button — opens modal */}
                                      {displaySections && (
                                        <div className="mt-2">
                                          <button
                                            onClick={() =>
                                              setReviewModalData({
                                                review: {
                                                  ...review,
                                                  sections: displaySections,
                                                } as Review,
                                                isFinal: !!isFinalReview,
                                              })
                                            }
                                            className={`inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-medium uppercase tracking-wider transition-colors ${
                                              isFinalReview
                                                ? "bg-green-50 border border-green-200 text-green-700 hover:bg-green-100"
                                                : "bg-blue-50 border border-blue-200 text-blue-700 hover:bg-blue-100"
                                            }`}
                                          >
                                            {isFinalReview ? (
                                              <UserCheck className="w-3.5 h-3.5" />
                                            ) : (
                                              <Bot className="w-3.5 h-3.5" />
                                            )}
                                            {isFinalReview
                                              ? "View detailed feedback"
                                              : "View AI feedback"}
                                            <ChevronRight className="w-3 h-3" />
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              )}

                            {/* Sent for review event */}
                            {review && review.status === "submitted_for_review" && (
                              <div className="relative flex items-start gap-4 pb-6">
                                <div className="relative z-10 flex-shrink-0 w-6 h-6 rounded-full bg-amber-50 border border-amber-300 flex items-center justify-center">
                                  <Clock className="w-3 h-3 text-amber-700" />
                                </div>
                                <div className="flex-1 min-w-0 pt-0.5">
                                  <span className="text-sm text-content-foreground font-medium">
                                    Sent for human review
                                  </span>
                                  <p className="text-xs text-content-muted mt-0.5">
                                    Your work package is in the review queue. You'll be notified
                                    when the final review is ready.
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      });
                    })()}

                    {/* Pending final review dot (if waiting) */}
                    {(isSubmittedForReview || userAssignment?.status === "final_review") &&
                      !isAdminConfirmed && (
                        <div className="relative flex items-start gap-4">
                          <div className="relative z-10 flex-shrink-0 w-6 h-6 rounded-full bg-content-subtle border border-content-border flex items-center justify-center">
                            <Circle className="w-3 h-3 text-content-muted" />
                          </div>
                          <div className="flex-1 min-w-0 pt-0.5">
                            <span className="text-sm text-content-muted">
                              Final review — pending
                            </span>
                          </div>
                        </div>
                      )}
                  </div>
                </div>
              </div>
            )}

            {/* ============================================================ */}
            {/* ACTION AREA                                                  */}
            {/* ============================================================ */}

            {uploadState === "idle" && submitError && (
              <div
                role="alert"
                className="mb-4 rounded-xl border border-red-200 bg-red-50 p-4 flex items-start gap-3"
              >
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-content-foreground font-medium">Submission failed</h3>
                  <p className="text-content-muted text-sm">{submitError}</p>
                  <p className="text-content-muted text-sm mt-1">
                    Your document is still attached below — press Submit again to retry.
                  </p>
                </div>
              </div>
            )}

            {uploadState === "idle" && (
              <>
                {/* No submission yet — show upload */}
                {!hasSubmissions && !deadlinePassed && (
                  <div>
                    <div className="mb-4">
                      <DocumentUpload
                        onFileSelect={handleFileSelect}
                        selectedFile={selectedFile}
                        disabled={false}
                        variant="glass"
                        className="w-full"
                      />
                    </div>
                    <div className="mb-4 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[11.5px] font-bold uppercase tracking-[.12em] text-content-muted">
                      <span>PDF format only</span>
                      <span>·</span>
                      <span>Max 1,200 words incl. references</span>
                      <span>·</span>
                      <span>Disclose AI use</span>
                    </div>
                    <button
                      onClick={handleSubmit}
                      disabled={!selectedFile || !userProfile}
                      className={`w-full rounded-[10px] py-3.5 text-[13px] font-extrabold uppercase tracking-[.12em] transition-all duration-200 ${
                        selectedFile && userProfile
                          ? "bg-primary text-primary-foreground hover:opacity-90 cursor-pointer"
                          : "bg-[#EAE6D6] text-content-muted cursor-not-allowed"
                      }`}
                    >
                      Submit for Review
                    </button>
                    <p className="mt-3.5 text-center font-serif text-[13px] italic text-content-muted">
                      Submit with a day to spare; feedback arrives in about 30 seconds, and you can
                      revise before the window closes.
                    </p>
                  </div>
                )}

                {/* No submission + deadline passed */}
                {!hasSubmissions && deadlinePassed && (
                  <div className="rounded-xl bg-red-50 border border-red-200 p-4 flex items-start gap-3">
                    <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="text-content-foreground font-medium">Deadline Passed</h3>
                      <p className="text-content-muted text-sm">
                        The submission deadline was {due.date} at {due.time}. No further submissions
                        are allowed.
                      </p>
                    </div>
                  </div>
                )}

                {/* AI reviewed — the participant can revise while the window
                    is open, up to the cohort's max_submissions total; at close
                    the version present is sent for sign-off automatically */}
                {isAiReviewed && canResubmit && !deadlinePassed && (
                  <div className="border-t border-content-border pt-6">
                    {!showResubmit ? (
                      <button
                        onClick={() => setShowResubmit(true)}
                        className="w-full rounded-[10px] border-[1.5px] border-[#013220] py-3.5 text-[13px] font-extrabold uppercase tracking-[.12em] text-[#013220] hover:bg-content-subtle transition-all duration-200 flex items-center justify-center gap-2"
                      >
                        <Upload className="w-4 h-4" />
                        Revise & Resubmit
                      </button>
                    ) : (
                      <div>
                        <h3 className="text-content-muted text-xs uppercase tracking-wider mb-4">
                          Upload Revised Document
                        </h3>
                        <div className="mb-4">
                          <DocumentUpload
                            onFileSelect={handleFileSelect}
                            selectedFile={selectedFile}
                            disabled={false}
                            variant="glass"
                            className="w-full"
                          />
                        </div>
                        <div className="mb-4 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[11.5px] font-bold uppercase tracking-[.12em] text-content-muted">
                          <span>PDF format only</span>
                          <span>·</span>
                          <span>Max 1,200 words incl. references</span>
                          <span>·</span>
                          <span>Disclose AI use</span>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-3">
                          <button
                            onClick={() => {
                              setShowResubmit(false);
                              setSelectedFile(null);
                            }}
                            className="flex-1 rounded-[10px] py-3.5 text-[13px] font-extrabold uppercase tracking-[.12em] border border-content-border text-content-foreground hover:bg-content-subtle transition-all duration-200"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleResubmit}
                            disabled={!selectedFile || !userProfile}
                            className={`flex-1 rounded-[10px] py-3.5 text-[13px] font-extrabold uppercase tracking-[.12em] transition-all duration-200 flex items-center justify-center gap-2 ${
                              selectedFile && userProfile
                                ? "bg-primary text-primary-foreground hover:opacity-90"
                                : "bg-[#EAE6D6] text-content-muted cursor-not-allowed"
                            }`}
                          >
                            <Send className="w-4 h-4" />
                            Resubmit
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {uploadState === "reviewing" && (
              <div className="flex flex-col items-center py-12">
                <Loader2 className="w-12 h-12 animate-spin text-brand mb-4" />
                <p className="text-content-foreground text-lg font-medium">{progressMessage}</p>
                <p className="text-content-muted text-sm mt-1">This may take a moment...</p>
              </div>
            )}
          </div>
        </>
      )}

      {/* Review Feedback Modal */}
      {reviewModalData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setReviewModalData(null)}
          />
          {/* Modal */}
          <div className="relative rounded-2xl bg-card border border-content-border shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-5 border-b border-content-border">
              <div className="flex items-center gap-3">
                {reviewModalData.isFinal ? (
                  <div className="w-8 h-8 rounded-lg bg-green-50 border border-green-300 flex items-center justify-center">
                    <UserCheck className="w-4 h-4 text-green-700" />
                  </div>
                ) : (
                  <div className="w-8 h-8 rounded-lg bg-blue-50 border border-blue-300 flex items-center justify-center">
                    <Bot className="w-4 h-4 text-blue-700" />
                  </div>
                )}
                <div>
                  <h2 className="text-content-foreground font-semibold">
                    {reviewModalData.isFinal ? "Final Review Feedback" : "AI Review Feedback"}
                  </h2>
                  {reviewModalData.isFinal ? (
                    <div className="flex items-center gap-2 mt-0.5">
                      <span
                        className={`text-lg font-bold ${
                          (
                            (reviewModalData.review.admin_final_score ??
                              reviewModalData.review.final_score) ||
                              0
                          ) >= 60
                            ? "text-green-700"
                            : ((reviewModalData.review.admin_final_score ??
                                  reviewModalData.review.final_score) ||
                                  0) >= 40
                              ? "text-amber-700"
                              : "text-red-700"
                        }`}
                      >
                        {reviewModalData.review.admin_final_score ??
                          reviewModalData.review.final_score}
                      </span>
                      <span className="text-content-muted text-sm">/100</span>
                    </div>
                  ) : (
                    <p className="mt-0.5 font-serif text-xs italic text-content-muted">
                      Feedback is revealed with the final review
                    </p>
                  )}
                </div>
              </div>
              <button
                onClick={() => setReviewModalData(null)}
                className="w-8 h-8 rounded-lg border border-content-border hover:bg-content-subtle flex items-center justify-center transition-colors"
                aria-label="Close"
              >
                <X className="w-4 h-4 text-content-foreground" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-5">
              {!reviewModalData.isFinal && (
                <div className="flex items-start gap-2 mb-4">
                  <Info className="w-3.5 h-3.5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-content-muted">
                    AI-generated feedback. Final grade will be provided by a human reviewer.
                  </p>
                </div>
              )}
              {renderReviewSections(reviewModalData.review, "modal", reviewModalData.isFinal)}
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-content-border">
              <button
                onClick={() => setReviewModalData(null)}
                className="w-full rounded-[10px] py-2.5 text-sm font-medium border border-content-border text-content-foreground hover:bg-content-subtle transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {userProfile && (
        <UnlockScreen
          open={showUnlock}
          onClose={() => setShowUnlock(false)}
          userId={userProfile.id}
        />
      )}
    </div>
  );
}
